import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import useLanguage from "../../../hooks/useLanguage";
import useContent from "../../../hooks/useContent";
import useToaster from "../../../hooks/useToaster";

export default function ModelsTab() {
  const [
    {
      pages: { settings: text },
    },
  ] = useLanguage();
  const { getModelSettings, updateModelSettings } = useContent();
  const { addToaster } = useToaster();

  const [features, setFeatures] = useState([]);
  const [saved, setSaved] = useState({});
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getModelSettings()
      .then((res) => {
        setFeatures(res?.features || []);
        setSaved(res?.settings || {});
        setDraft(res?.settings || {});
      })
      .catch(() => addToaster({ message: text.models.loadFailed, severity: "error" }))
      .finally(() => setLoading(false));
  }, []);

  // Only changed keys are sent, so two admins editing different features don't
  // overwrite each other.
  const changed = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(draft).filter((k) => draft[k] !== saved[k]).map((k) => [k, draft[k]])
      ),
    [draft, saved]
  );

  const save = async () => {
    setSaving(true);
    try {
      const next = await updateModelSettings(changed);
      setSaved(next);
      setDraft(next);
      addToaster({ message: text.success.saved, severity: "success" });
    } catch {
      addToaster({ message: text.errors.saveFailed, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Stack spacing={2} sx={{ maxWidth: 720 }}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} variant="rounded" height={56} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: 720 }}>
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          {text.models.heading}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {text.models.help}
        </Typography>
      </Box>

      <Stack divider={<Divider flexItem />} spacing={0}>
        {features.map((f) => {
          const copy = text.models.features[f.key] || {};
          const value = draft[f.key] ?? f.default;
          return (
            <Stack
              key={f.key}
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              sx={{ py: 2 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2">{copy.label || f.key}</Typography>
                  {value !== f.default && (
                    <Chip label={text.models.overridden} size="small" variant="outlined" />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {copy.description}
                </Typography>
              </Box>
              <TextField
                select
                size="small"
                value={value}
                onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                disabled={saving}
                sx={{ minWidth: 240 }}
              >
                {f.catalog.map((id) => (
                  <MenuItem key={id} value={id}>
                    {id}
                    {id === f.default ? ` — ${text.models.defaultTag}` : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          );
        })}
      </Stack>

      <Box>
        <Button
          variant="contained"
          onClick={save}
          disabled={!Object.keys(changed).length || saving}
        >
          {text.save}
        </Button>
      </Box>
    </Stack>
  );
}
